/* eslint-disable no-param-reassign */
import set from 'lodash/set';
import find from 'lodash/find';
import mapValues from 'lodash/mapValues';
import isPlainObject from 'lodash/isPlainObject';
import {
  isPending,
  isNotFound,
  isSimpleKey,
  isRef,
  normaliseKey,
} from '../../../muster-utils';

import {
  FRAGMENTS_SYMBOL,
  isList,
  isMap,
  isCallOrSet,
  isFragment,
  isDeferred,
  isLeafNode,
  isStaticList,
  joinPathSegments,
  parseStaticRange,
  when,
  otherwise,
} from '../utils';

function getPathValue(cache, path) {
  return cache.get(path);
}

function updateMemo(key, memo, result) {
  if (result.isReady === false) {
    memo.isReady = false;
  }
  set(memo.state, key, result.state);
  return memo;
}

function getLeafState(pathValues, prefixedPath) {
  const value = getPathValue(pathValues, prefixedPath);
  return {
    isReady: !isPending(value),
    state: parseValue(value),
  };
}

function parseValue(value) {
  if (isNotFound(value)) {
    return null;
  }
  if (isPending(value)) {
    return undefined;
  }
  return value;
}

function getDeferredState(muster, node, pathValues, targetPath) {
  function getState(branch) {
    return branch.isReady ? branch.state : undefined;
  }

  // Handle deferred leaf nodes
  // E.G. todo: { description: defer(true) }
  if (!isPlainObject(node)) {
    return {
      isReady: true,
      state: parseValue(getPathValue(pathValues, targetPath)),
    };
  }

  if (isList(node)) {
    const branch = getListState(muster, node, pathValues, targetPath);
    return {
      isReady: true,
      state: getState(branch),
    };
  }

  if (isMap(node)) {
    const branch = getMapState(muster, node, pathValues, targetPath);
    return {
      isReady: true,
      state: getState(branch),
    };
  }

  // Evaluate the branch
  const branch = createQueryState(muster, node, pathValues, targetPath);
  // Cut off the whole branch if it's not ready
  return {
    isReady: true,
    state: getState(branch),
  };
}

function getFragmentState(muster, node, pathValues, parentPath) {
  const { $fields } = node;

  // Determine if the fragment is ready
  const result = createQueryState(muster, $fields, pathValues, parentPath);

  return {
    isReady: result.isReady,
    state: {
      $path: parentPath,
      $pathValues: pathValues,
      $type: 'fragment',
    },
  };
}

function getListState(muster, node, pathValues, parentPath) {
  if (isStaticList(node)) return getStaticListState(muster, node, pathValues, parentPath);
  return getDynamicListState(muster, node, pathValues, parentPath);
}

function getRefValue(pathValues, maybeRef) {
  return isRef(maybeRef) ? pathValues.get(maybeRef.value) : maybeRef;
}

function getDynamicListState(muster, node, pathValues, parentPath) {
  const rangeValues = mapValues(node.$range, (value) =>
    getRefValue(pathValues, value)
  );

  const rangeIsReady = Object.keys(rangeValues)
    .map((key) => rangeValues[key])
    .every((value) => !isPending(value) && value !== undefined);

  if (!rangeIsReady) {
    return { isReady: false, state: undefined };
  }

  const newNode = {
    ...node,
    $range: rangeValues,
  };
  return getStaticListState(muster, newNode, pathValues, parentPath);
}

function getStaticListState(muster, node, pathValues, parentPath) {
  const state = [];
  const isFlatList = (node.$node === undefined);

  const { from, to } = parseStaticRange(node.$range);
  if (to < from) {
    // If the `to` value is less than the `from` value we don't try and fetch
    // the list at all so always return an empty list.
    return { isReady: true, state };
  }

  let isReady = true;

  for (let i = from; i <= to; i++) {
    let value;
    const prefixedPath = `${parentPath}[${i}]`;
    if (isFlatList) {
      value = getPathValue(pathValues, prefixedPath);
      if (isPending(value)) {
        isReady = false;
      }
    } else {
      const result = createQueryState(muster, node.$node, pathValues, prefixedPath);
      ({ state: value } = result);
      if (result.isReady === false) {
        isReady = false;
      }
    }

    // TODO check if value is defined?
    state.push(value);
  }
  return { isReady, state };
}

function getMapState(muster, node, pathValues, parentPath) {
  const isFlatMap = (node.$node === undefined);
  let isReady = true;

  const state = node.$keys.reduce((memo, key) => {
    let value;
    const glue = isSimpleKey(key) ? '.' : '';
    const prefixedPath = parentPath + glue + normaliseKey(key);
    if (isFlatMap) {
      value = getPathValue(pathValues, prefixedPath);
      if (isPending(value)) {
        isReady = false;
      }
    } else {
      const result = createQueryState(muster, node.$node, pathValues, prefixedPath);
      ({ state: value } = result);
      if (result.isReady === false) {
        isReady = false;
      }
    }

    return {
      ...memo,
      [key]: value,
    };
  }, {});

  return { isReady, state };
}

function createCallOrSetNode(muster, node, parentPath) {
  // bind call or set to muster
  const { actionType, path } = getAction(node);
  const prefixedPath = joinPathSegments(parentPath, path);

  if (actionType === '$set') {
    return (value) => (
      // TODO validate that only one arg is passed
      muster.set(prefixedPath, value)
    );
  }

  return (...args) => (
    muster.call(prefixedPath, ...args)
  );
}

function getAction(actionObject) {
  // By this point we should have validated the action object so no need for any
  // more checks here

  const keys = Object.keys(actionObject);
  const actionType = keys[0];

  return {
    actionType,
    path: actionObject[actionType],
  };
}

// TODO memoize: DGUI-787
export default function createQueryState(muster, query, pathValues, parentPath = null) {
  if (isDeferred(query)) {
    return getDeferredState(muster, query.$node, pathValues, parentPath);
  }

  if (isList(query)) {
    return getListState(muster, query, pathValues, parentPath);
  }

  if (isMap(query)) {
    return getMapState(muster, query, pathValues, parentPath);
  }

  const { isReady, state } = Object.keys(query).reduce((memo, key) => {
    const node = query[key];
    const prefixedPath = joinPathSegments(parentPath, key);

    const updater = updateMemo.bind(null, key, memo);
    const fragmentUpdater = updateMemo.bind(null, `${FRAGMENTS_SYMBOL}.${key}`, memo);

    return find([
      when(isLeafNode, () => updater(getLeafState(pathValues, prefixedPath))),
      when(isCallOrSet, () => updater({ state: createCallOrSetNode(muster, node, parentPath) })),
      when(isList, () => updater(getListState(muster, node, pathValues, prefixedPath))),
      when(isMap, () => updater(getMapState(muster, node, pathValues, prefixedPath))),
      when(isFragment, () =>
        fragmentUpdater(getFragmentState(muster, node, pathValues, parentPath))
      ),
      when(isDeferred, () =>
        updater(getDeferredState(muster, node, pathValues, prefixedPath))
      ),
      otherwise(() => updater(createQueryState(muster, node, pathValues, prefixedPath))),
    ], (transformer) => transformer.predicate(node)).func(memo);
  }, { isReady: true, state: {} });

  return {
    isReady,
    state,
  };
}
